// components/inventory/ProductCard.tsx
import { useAppTheme } from "@/hooks/useAppTheme";
import { useAppDispatch } from "@/store/hooks";
import { deleteProduct, updateProduct } from "@/store/slices/productSlice";
import { Ionicons } from "@expo/vector-icons";
import { router } from "expo-router";
import { useState } from "react";
import { Alert, StyleSheet, Text, TouchableOpacity, View } from "react-native";

export default function ProductCard({ product, onRefresh }: any) {
  const { colors, spacing } = useAppTheme();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const isActive = product.deletedAt === null; // or product.status if present; API uses deletedAt to indicate active/inactive

  const handleEdit = () =>
    router.push(`/(app)/inventory/products/${product.id}`);
  const handleToggle = async () => {
    // Toggle status via update (soft delete: set deletedAt to date or null)
    const newDeletedAt = isActive ? new Date().toISOString() : null;
    // Assuming backend supports deletedAt field for soft delete
    // For now, we'll just simulate; adjust based on actual API.
    setLoading(true);
    const result = await dispatch(
      updateProduct({ id: product.id, data: { deletedAt: newDeletedAt } }),
    );
    if (updateProduct.fulfilled.match(result)) onRefresh();
    setLoading(false);
  };
  const handleDelete = () => {
    Alert.alert("Delete", `Delete ${product.name}?`, [
      { text: "Cancel", style: "cancel" },
      {
        text: "Delete",
        style: "destructive",
        onPress: async () => {
          setLoading(true);
          const result = await dispatch(deleteProduct(product.id));
          if (deleteProduct.fulfilled.match(result)) onRefresh();
          setLoading(false);
        },
      },
    ]);
  };

  const unitSymbol = product.Unit?.symbol || "";
  const totalBaseQuantity = product.packQuantity * (product.stockQuantity || 0); // stockQuantity may not be present in API; adjust if needed

  const styles = StyleSheet.create({
    card: {
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: spacing.md,
      marginBottom: spacing.md,
      borderWidth: 1,
      borderColor: colors.border,
    },
    header: { flexDirection: "row", justifyContent: "space-between" },
    name: { fontSize: 16, fontWeight: "600", color: colors.text },
    sku: { fontSize: 12, color: colors.textLight, marginTop: 2 },
    price: {
      fontSize: 14,
      fontWeight: "500",
      color: colors.primary,
      marginTop: 4,
    },
    packInfo: { fontSize: 12, color: colors.textLight, marginTop: 2 },
    gst: { fontSize: 12, color: colors.textLight, marginTop: 2 },
    statusBadge: { flexDirection: "row", alignItems: "center", marginTop: 8 },
    statusDot: { width: 8, height: 8, borderRadius: 4, marginRight: 6 },
    statusText: { fontSize: 12, fontWeight: "500" },
    actions: {
      flexDirection: "row",
      marginTop: spacing.md,
      paddingTop: spacing.sm,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    actionButton: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      paddingVertical: 6,
    },
    actionText: { fontSize: 12, marginLeft: 4, color: colors.textLight },
  });

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <View>
          <Text style={styles.name}>{product.name}</Text>
          <Text style={styles.sku}>SKU: {product.sku}</Text>
        </View>
        <TouchableOpacity onPress={handleEdit} disabled={loading}>
          <Ionicons name="chevron-forward" size={20} color={colors.textLight} />
        </TouchableOpacity>
      </View>
      <Text style={styles.price}>
        ₹{product.price} / {product.packQuantity}
        {unitSymbol}
      </Text>
      <Text style={styles.packInfo}>
        Pack Qty: {product.packQuantity}
        {unitSymbol}
      </Text>
      <Text style={styles.gst}>GST: {product.gstPercent}%</Text>
      <View style={styles.statusBadge}>
        <View
          style={[
            styles.statusDot,
            { backgroundColor: isActive ? "#22c55e" : "#ef4444" },
          ]}
        />
        <Text
          style={[
            styles.statusText,
            { color: isActive ? "#22c55e" : "#ef4444" },
          ]}
        >
          {isActive ? "Active" : "Inactive"}
        </Text>
      </View>
      <View style={styles.actions}>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={handleEdit}
          disabled={loading}
        >
          <Ionicons name="create-outline" size={18} color={colors.primary} />
          <Text style={styles.actionText}>Edit</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={handleToggle}
          disabled={loading}
        >
          <Ionicons
            name={isActive ? "eye-off-outline" : "eye-outline"}
            size={18}
            color="#f59e0b"
          />
          <Text style={styles.actionText}>
            {isActive ? "Deactivate" : "Activate"}
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={handleDelete}
          disabled={loading}
        >
          <Ionicons name="trash-outline" size={18} color={colors.danger} />
          <Text style={[styles.actionText, { color: colors.danger }]}>
            Delete
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}
